import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { caseStudyForStudent } from '../case-study/case-study.js';
import { goalTraceability } from './goal-links.js';
import { buildPeiDashboard } from './peiAnalytics.js';

function fmt(value) {
  if (!value) return '-';
  return new Intl.DateTimeFormat('pt-BR').format(new Date(`${value}T12:00:00`));
}

export function PeiSmartPage({ data = {}, studentId: explicitStudentId = null }) {
  const params = useParams();
  const studentId = explicitStudentId || params.studentId;
  const student = useMemo(() => (data.students || []).find((item) => item.id === studentId), [data.students, studentId]);
  const pei = useMemo(() => (data.peis || []).find((item) => item.studentId === studentId) || { metas: [] }, [data.peis, studentId]);
  const logs = useMemo(() => (data.logs || []).filter((item) => item.studentId === studentId), [data.logs, studentId]);
  const caseStudy = useMemo(() => caseStudyForStudent(data.caseStudies, studentId), [data.caseStudies, studentId]);
  const dashboard = useMemo(() => buildPeiDashboard(pei, logs), [pei, logs]);

  if (!student) return <section className="pei-smart-page"><section className="pei-smart-card"><h1>Aluno não encontrado</h1><Link to="/professor">Voltar</Link></section></section>;

  return (
    <section className="pei-smart-page">
      <header className="pei-smart-header">
        <div><p>PEI inteligente</p><h1>{student.nome}</h1><span>Progresso calculado a partir das metas e dos atendimentos registrados.</span></div>
        <Link to={`/aluno/${student.id}/pei`}>Voltar à ficha</Link>
      </header>

      <section className="pei-smart-metrics">
        <article><strong>{dashboard.averageProgress}%</strong><span>progresso médio</span></article>
        <article><strong>{dashboard.active}</strong><span>metas ativas</span></article>
        <article><strong>{dashboard.completed}</strong><span>metas concluídas</span></article>
        <article><strong>{dashboard.overdue}</strong><span>metas vencidas</span></article>
        <article><strong>{dashboard.withoutService}</strong><span>sem atendimento</span></article>
      </section>

      {!dashboard.goals.length ? <section className="pei-smart-card"><h2>Nenhuma meta cadastrada</h2><p>Cadastre metas SMART na ficha do aluno para iniciar o acompanhamento automático.</p></section> : null}

      <section className="pei-smart-list">
        {dashboard.goals.map((goal) => {
          const trace = goalTraceability(goal, { caseStudy, logs });
          return <article className="pei-goal-card" data-status={goal.status} key={goal.id}>
            <header><div><span className="pei-status">{goal.status.replaceAll('_', ' ')}</span><h2>{goal.descricao || goal.titulo || 'Meta sem descrição'}</h2></div><strong>{goal.progress}%</strong></header>
            <div className="pei-progress"><i style={{ width: `${goal.progress}%` }} /></div>
            <div className="pei-trace">
              <div><span>Barreira</span><p>{trace.barrier || 'Ainda não registrada'}</p><small>{trace.barrierSource}</small></div>
              <div><span>Atividades relacionadas</span><p>{trace.evidence.activities.join(', ') || 'Nenhuma atividade relacionada ainda'}</p></div>
              <div><span>Evidências recentes</span>{trace.evidence.items.length ? <ul>{trace.evidence.items.map((item, index) => <li key={`${item.logId}:${item.activity}:${index}`}><b>{fmt(item.date)}</b> — {item.activity} — {item.result}</li>)}</ul> : <p>Aguardando atendimento relacionado.</p>}{trace.evidence.alert && <small className="pei-alert">{trace.evidence.alert}</small>}</div>
            </div>
            <dl>
              <div><dt>Linha de base</dt><dd>{goal.baseline}%</dd></div>
              <div><dt>Nível atual</dt><dd>{goal.current}%</dd></div>
              <div><dt>Alvo</dt><dd>{goal.target}%</dd></div>
              <div><dt>Atendimentos relacionados</dt><dd>{goal.relatedLogs}</dd></div>
              <div><dt>Último atendimento</dt><dd>{fmt(goal.lastDate)}</dd></div>
              <div><dt>Prazo</dt><dd>{fmt(goal.prazo)}</dd></div>
            </dl>
            <p><b>Habilidades:</b> {goal.habilidades.join(', ') || 'não vinculadas'}</p>
            <p className="pei-recommendation"><b>Próxima ação:</b> {goal.recommendation}</p>
          </article>;
        })}
      </section>
    </section>
  );
}

export default PeiSmartPage;
